/**
 * React-query hooks for the bank-reconciliation review queue (COL-350).
 *
 * `finapi-sync-tick` + `_shared/bank-reconcile.ts` park anything the engine
 * can't confidently apply as `reconciliation_matches` rows with
 * status='needs_review'. Admins decide via `confirm_reconciliation_match` /
 * `reject_reconciliation_match` (see migration `20260804120000_...`) — both
 * SECURITY DEFINER RPCs that re-check the admin role server-side, so these
 * hooks are a thin client over that gate rather than the enforcement point.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invalidateOwnerCaches } from "@/hooks/v2/owner-detail-keys";
import { PROPERTY_RENT_PAID_STATS_KEY } from "@/hooks/v2/usePropertyRentPaidStats";
import {
  decisionPayment,
  parseBookedSplit,
  type BookedTransferSplit,
} from "@/lib/transfer-split";
import { fetchAllRows } from "@/lib/fetch-all-rows";
import { getExpectedDepositEUR } from "@/lib/rent";
import type { QueryPollOptions } from "@/lib/query-poll";
import { useMemo } from "react";
import { useTestAccountFilters } from "@/hooks/v2/useTestAccountFilters";
import { BRAIN_HIDES_TEST_DATA, isBrainTestRow } from "@/lib/brain/test-data";

export const RECONCILIATION_KEYS = {
  reviewQueue: () => ["v2", "reconciliation", "review-queue"] as const,
  reviewCount: () => ["v2", "reconciliation", "review-count"] as const,
  openInvoices: () => ["v2", "reconciliation", "open-invoices"] as const,
  transferPaidMonths: (bankTransactionId: string | null) =>
    [
      "v2",
      "reconciliation",
      "transfer-paid-months",
      bankTransactionId,
    ] as const,
};

export type ReconciliationBankTransaction = {
  id: string;
  finapi_transaction_id: number;
  amount_cents: number;
  currency: string;
  counterpart_name: string | null;
  counterpart_iban: string | null;
  purpose: string | null;
  booking_date: string | null;
  value_date: string | null;
};

export type ReconciliationCandidatePayment = {
  id: string;
  user_id: string | null;
  booking_id: number | null;
  type: string;
  amount_cents: number;
  /** COL-417: cents already received against this invoice. */
  received_cents?: number | null;
  currency: string;
  due_date: string | null;
  status: string;
  /** COL-508: a card/SEPA charge is attached — the transfer split skips the month. */
  stripe_payment_intent_id?: string | null;
  stripe_charge_id?: string | null;
  bookings?: {
    id: number;
    /** COL-508: the tenancy's Mahngebühren pot — paid right after the oldest open rent. */
    accumulated_fees_eur?: number | null;
    rooms?: {
      id: number;
      title: string | null;
      properties?: {
        id: number;
        title: string | null;
        city: string | null;
      } | null;
    } | null;
  } | null;
};

export type ReconciliationMatchRow = {
  id: string;
  bank_transaction_id: string;
  payment_id: string | null;
  /**
   * COL-508: the invoice this row was confirmed/assigned against. `payment_id`
   * is half of a unique (transaction, payment) index, so an assign onto an
   * invoice a sibling row of the same transfer already names cannot move it —
   * this column is not indexed that way and always holds the real target. NULL
   * on undecided rows and on rows decided before COL-508; read it through
   * `decisionPayment()` below, never on its own.
   */
  decided_payment_id?: string | null;
  booking_id: number | null;
  match_method: string | null;
  confidence: number | null;
  status: string;
  /**
   * COL-508: the part of the bank transaction this row accounts for. NULL = the
   * whole transaction. Set on a remainder row (what is still to place) and on
   * every decided row (what that decision booked).
   */
  amount_cents?: number | null;
  review_reason: string | null;
  created_at: string;
  decided_at: string | null;
  bank_transactions: ReconciliationBankTransaction | null;
  payments: ReconciliationCandidatePayment | null;
  /** COL-508: the `decided_payment_id` embed. See that column. */
  decided_payments?: ReconciliationCandidatePayment | null;
  // Enriched client-side, mirroring usePaymentsAdmin's tenant lookup.
  tenantName?: string | null;
  tenantEmail?: string | null;
};

/**
 * COL-508: which invoice a row should be READ as pointing at. Lives in
 * `@/lib/transfer-split` (no Supabase client at import time, so tests can use
 * it); re-exported here because this module owns the row type.
 */
export { decisionPayment } from "@/lib/transfer-split";

/**
 * Tenant names for a set of user ids, however many there are.
 *
 * Two caps to respect, not one: `.in()` travels in the query string, so an
 * unbounded id list builds an over-long URL — hence the batches; and each
 * batch's response is still subject to the server row cap, so each is read
 * through `fetchAllRows` rather than assumed to come back whole. A dropped
 * profile leaves an invoice nameless and unfindable in the picker's search.
 *
 * Ids with no profile row simply do not come back — that is not truncation,
 * which is why completeness cannot be checked by counting.
 */
async function fetchProfiles(
  userIds: readonly string[]
): Promise<Array<{ id: string; name: string | null; email: string | null }>> {
  const ID_BATCH = 200; // a uuid costs ~37 characters of URL
  const out: Array<{ id: string; name: string | null; email: string | null }> =
    [];
  for (let i = 0; i < userIds.length; i += ID_BATCH) {
    const batch = userIds.slice(i, i + ID_BATCH);
    out.push(
      ...(await fetchAllRows<{
        id: string;
        name: string | null;
        email: string | null;
      }>("useReconciliation profiles", (from, size) =>
        supabase
          .from("profiles")
          .select("id, name, email")
          .in("id", batch)
          .order("id", { ascending: true })
          .range(from, from + size - 1)
      ))
    );
  }
  return out;
}

/** Most recent decisions kept visible in the Closed tab; older history stays
 * queryable in the DB but doesn't need to ship to the browser. */
const CLOSED_ROWS_LIMIT = 300;

const PAYMENT_EMBED = `id, user_id, booking_id, type, amount_cents, received_cents, currency, due_date, status, stripe_payment_intent_id, stripe_charge_id, bookings:booking_id ( id, accumulated_fees_eur, rooms:room_id ( id, title, properties:property_id ( id, title, city ) ) )`;

const MATCH_SELECT = `id, bank_transaction_id, payment_id, decided_payment_id, booking_id, match_method, confidence, status, amount_cents, review_reason, created_at, decided_at,
 bank_transactions:bank_transaction_id ( id, finapi_transaction_id, amount_cents, currency, counterpart_name, counterpart_iban, purpose, booking_date, value_date ),
 payments:payment_id ( ${PAYMENT_EMBED} ),
 decided_payments:decided_payment_id ( ${PAYMENT_EMBED} )`;

/**
 * COL-593: a reconciliation row Brain and the reconciliation tab hide on prod —
 * the invoice it was matched against (decided, else candidate) belongs to a
 * test/CoLiviq tenant or a demo building. Keyed off the same invoice
 * `decisionPayment` already names the tenant from, so a row that shows a test
 * name is the row that gets hidden. Undecided transfers with no candidate
 * invoice carry no account, so they stay — there is nothing to call them test.
 */
export function isReconTestRow(
  row: ReconciliationMatchRow,
  testEmails: readonly string[]
): boolean {
  const title =
    decisionPayment(row)?.bookings?.rooms?.properties?.title ?? null;
  return isBrainTestRow(row.tenantEmail, title, testEmails);
}

/**
 * All reconciliation matches for the Open/Closed tabs, joined to their bank
 * transaction and candidate payment/booking/room/property for display.
 * Open (needs_review) rows are fetched WITHOUT a cap — an unaddressed
 * transfer must never fall out of the queue no matter how old — while the
 * decided history is capped at the most recent CLOSED_ROWS_LIMIT decisions.
 *
 * COL-593: on prod, test/CoLiviq rows are dropped before anyone sees them, in
 * both consumers (Brain cards and the reconciliation tab). Static rules
 * (is-test-account domain/seed + demo-property name) apply at once; the admins'
 * dynamic test emails refine as `useTestAccountFilters` resolves. Dev keeps
 * every row so the queue can still be exercised there.
 */
export function useReconciliationReviewQueue(
  enabled = true,
  poll?: QueryPollOptions
) {
  const query = useQuery({
    queryKey: RECONCILIATION_KEYS.reviewQueue(),
    enabled,
    ...poll,
    queryFn: async (): Promise<ReconciliationMatchRow[]> => {
      const [open, closed] = await Promise.all([
        supabase
          .from("reconciliation_matches")
          .select(MATCH_SELECT)
          .eq("status", "needs_review")
          .order("created_at", { ascending: true }),
        supabase
          .from("reconciliation_matches")
          .select(MATCH_SELECT)
          .neq("status", "needs_review")
          .order("decided_at", { ascending: false })
          .limit(CLOSED_ROWS_LIMIT),
      ]);
      if (open.error) throw open.error;
      if (closed.error) throw closed.error;
      // MATCH_SELECT splices PAYMENT_EMBED in twice, which the generated
      // types can't follow through the embeds.
      const rows = [
        ...(open.data ?? []),
        ...(closed.data ?? []),
      ] as unknown as ReconciliationMatchRow[];

      // COL-508: keyed off the DECIDED invoice, so a row whose candidate
      // pointer could not move still names the tenant its money reached.
      const userIds = [
        ...new Set(
          rows.map((r) => decisionPayment(r)?.user_id).filter(Boolean)
        ),
      ] as string[];
      if (userIds.length === 0) return rows;

      const profiles = await fetchProfiles(userIds);

      const byId = new Map<
        string,
        { name: string | null; email: string | null }
      >();
      (profiles ?? []).forEach((p: any) =>
        byId.set(p.id, { name: p.name, email: p.email })
      );

      return rows.map((r) => {
        const userId = decisionPayment(r)?.user_id;
        return {
          ...r,
          tenantName: userId ? (byId.get(userId)?.name ?? null) : null,
          tenantEmail: userId ? (byId.get(userId)?.email ?? null) : null,
        };
      });
    },
  });

  // COL-593: hide test/CoLiviq rows on prod, in every caller at once.
  const hideTest = enabled && BRAIN_HIDES_TEST_DATA;
  const { data: filters } = useTestAccountFilters({ enabled: hideTest });
  const data = useMemo(() => {
    if (!hideTest || !query.data) return query.data;
    const emails = (filters ?? []).map((f) => f.email);
    return query.data.filter((r) => !isReconTestRow(r, emails));
  }, [hideTest, query.data, filters]);

  return { ...query, data } as typeof query;
}

export type OpenInvoice = {
  id: string;
  user_id: string | null;
  booking_id: number | null;
  amount_cents: number;
  /** COL-417: cents already received against this invoice. */
  received_cents?: number | null;
  currency: string;
  due_date: string | null;
  status: string;
  /** 'rent' | 'deposit'. A deposit books as one invoice (no cascade); the split preview skips it. */
  type?: string | null;
  /** COL-508: a card/SEPA charge is attached — the transfer split skips the month. */
  stripe_payment_intent_id?: string | null;
  stripe_charge_id?: string | null;
  bookings?: {
    id: number;
    /** COL-508: the tenancy's Mahngebühren pot. */
    accumulated_fees_eur?: number | null;
    rooms?: {
      id: number;
      title: string | null;
      properties?: { id: number; title: string | null } | null;
    } | null;
  } | null;
  tenantName?: string | null;
  tenantEmail?: string | null;
};

/**
 * COL-398 follow-up: a deposit has no payments row until it is marked paid, so
 * a deposit transfer that arrives while the deposit is still "pending" has
 * nothing to assign to in the picker. We synthesise one pending-deposit entry
 * per active booking that owes a deposit and has no live deposit row yet; its
 * id carries this prefix so `assign` knows to materialise the real row first.
 */
export const PENDING_DEPOSIT_PREFIX = "pending-deposit:";

/**
 * Booking statuses that can still owe an uncollected deposit, so a deposit
 * bank transfer can arrive against them. A deposit is owed across the whole
 * live tenancy — from applied (`pending`) through the move-out wind-down — not
 * just the `confirmed`/`active`/`approved` of `ACTIVE_BOOKING_STATUSES` (that
 * set drives reassignment). Gating the picker on the narrower set dropped
 * `pending` bookings before the deposit resolver ran, so e.g. Chaimaa Radid's
 * still-pending €900 deposit never appeared (COL-398). Terminal states
 * (moved_out, cancelled, rejected, …) are excluded: a dead booking owes nothing.
 */
export const DEPOSIT_OWING_BOOKING_STATUSES = [
  "pending",
  "approved",
  "confirmed",
  "active",
  "notice_given",
  "move_out_pending",
] as const;

/**
 * The active booking shape the synthetic pending-deposit pool needs.
 *
 * The owed deposit is the CONTRACTUAL figure getExpectedDepositEUR resolves —
 * booking.deposit_amount, else rooms.deposit_amount, else 2× rent, minus any
 * deposit discount — NOT the raw deposit_amount column, which is null for a
 * tenant whose deposit falls back to the room base or the rent multiple (e.g.
 * Chaimaa Radid's €900). So this carries every field that resolver reads.
 */
export type DepositOwingBooking = {
  id: number;
  user_id: string | null;
  deposit_amount: number | null;
  monthly_rent?: number | null;
  deposit_discount_mode?: string | null;
  deposit_discount_value?: number | null;
  rooms?: {
    id: number;
    title: string | null;
    price?: number | null;
    deposit_amount?: number | null;
    properties?: { id: number; title: string | null } | null;
  } | null;
};

/**
 * Build the synthetic pending-deposit entries for the picker: one per booking
 * that owes a deposit (getExpectedDepositEUR > 0, i.e. the contractual figure
 * including the room-base / 2×-rent fallback) and has no live deposit
 * payments row yet. Pure so it can be unit-tested without a DB. See
 * PENDING_DEPOSIT_PREFIX.
 */
export function syntheticPendingDeposits(
  bookings: readonly DepositOwingBooking[],
  bookingIdsWithLiveDeposit: ReadonlySet<number>,
  nameByUser: ReadonlyMap<string, { name: string | null; email: string | null }>
): OpenInvoice[] {
  const out: OpenInvoice[] = [];
  for (const b of bookings) {
    if (bookingIdsWithLiveDeposit.has(b.id)) continue;
    // Contractual deposit, not the raw column — see DepositOwingBooking.
    const cents = Math.round(getExpectedDepositEUR(b) * 100);
    if (cents <= 0) continue;
    out.push({
      id: `${PENDING_DEPOSIT_PREFIX}${b.id}`,
      user_id: b.user_id,
      booking_id: b.id,
      amount_cents: cents,
      received_cents: 0,
      currency: "EUR",
      due_date: null,
      status: "pending",
      type: "deposit",
      bookings: { id: b.id, rooms: b.rooms ?? null },
      tenantName: b.user_id ? (nameByUser.get(b.user_id)?.name ?? null) : null,
      tenantEmail: b.user_id
        ? (nameByUser.get(b.user_id)?.email ?? null)
        : null,
    });
  }
  return out;
}

/**
 * Every open rent invoice (pending/scheduled/overdue), enriched with the
 * tenant's profile name — the candidate pool for manually assigning a bank
 * transaction (COL-398). Same scale as the sync tick's matching context:
 * one bounded query, filtered client-side by the picker's search box.
 * Also the tenancy view the COL-508 split preview reads (every open month of
 * the confirmed/assigned invoice's booking), so it is fetched while the Open
 * tab has rows to decide, not only while the assign dialog is open.
 */
/**
 * Months a bank transaction has ALREADY paid rent onto.
 *
 * `decide_reconciliation_split` skips those when it plans the next decision on
 * the same transfer, so the browser preview has to skip them too or it offers
 * a month the RPC will pass over. Only a second decision of one transfer can
 * reach this — assigning a remainder row — so it is fetched only while the
 * assign dialog is open on a row that accounts for part of its transfer.
 */
export function useTransferPaidMonths(
  bankTransactionId: string | null,
  enabled: boolean
) {
  return useQuery({
    queryKey: RECONCILIATION_KEYS.transferPaidMonths(bankTransactionId),
    enabled: enabled && !!bankTransactionId,
    staleTime: 30_000,
    queryFn: async (): Promise<string[]> => {
      const rows = await fetchAllRows<{ payment_id: string }>(
        "useTransferPaidMonths",
        (from, size) =>
          supabase
            .from("payment_receipts")
            .select("payment_id")
            .eq("bank_transaction_id", bankTransactionId)
            .eq("applies_to", "rent")
            .order("payment_id", { ascending: true })
            .range(from, from + size - 1)
      );
      return [...new Set(rows.map((r) => String(r.payment_id)))];
    },
  });
}

/**
 * COL-593: the picker's candidate pool hides the same test/CoLiviq rows the
 * review queue does — a real and a synthetic deposit entry both carry
 * `tenantEmail` and the property title at `bookings.rooms.properties.title`,
 * so one predicate covers both. Dev (BRAIN_HIDES_TEST_DATA false) keeps them.
 */
export function isOpenInvoiceTestRow(
  inv: OpenInvoice,
  testEmails: readonly string[]
): boolean {
  const title = inv.bookings?.rooms?.properties?.title ?? null;
  return isBrainTestRow(inv.tenantEmail ?? null, title, testEmails);
}

export function useOpenInvoices(enabled: boolean) {
  const query = useQuery({
    queryKey: RECONCILIATION_KEYS.openInvoices(),
    enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<OpenInvoice[]> => {
      // The COL-508 split preview treats this list as the tenancy's COMPLETE
      // set of open months — a truncated page would show omitted months as
      // credit or remainder while the RPC actually pays them. See fetchAllRows
      // for why the cap must not be assumed.
      const [data, depositBookings, liveDepositRows] = await Promise.all([
        fetchAllRows<OpenInvoice>("useOpenInvoices", (from, size) =>
          supabase
            .from("payments")
            .select(
              `id, user_id, booking_id, amount_cents, received_cents, currency, due_date, status, type, stripe_payment_intent_id, stripe_charge_id,
           bookings:booking_id ( id, accumulated_fees_eur, rooms:room_id ( id, title, properties:property_id ( id, title ) ) )`
            )
            // COL-398: deposits are assignable too (as a single invoice); the
            // split preview filters them out of the rent cascade by type.
            .in("type", ["rent", "deposit"])
            // COL-417: part-paid months are assignable now (the RPC accepts
            // them), and are often exactly where an unmatched transfer belongs.
            .in("status", ["pending", "scheduled", "partially_paid", "overdue"])
            .order("due_date", { ascending: true })
            .order("id", { ascending: true })
            .range(from, from + size - 1)
        ),
        // COL-398 follow-up: active bookings that owe a deposit — the pool the
        // synthetic pending-deposit entries are built from (a pending deposit
        // has no payments row to appear above).
        fetchAllRows<DepositOwingBooking>(
          "useOpenInvoices deposit bookings",
          (from, size) =>
            supabase
              .from("bookings")
              .select(
                "id, user_id, deposit_amount, monthly_rent, deposit_discount_mode, deposit_discount_value, rooms:room_id ( id, title, price, deposit_amount, properties:property_id ( id, title ) )"
              )
              .is("admin_archived_at", null)
              .in(
                "status",
                DEPOSIT_OWING_BOOKING_STATUSES as unknown as string[]
              )
              // No .gt("deposit_amount", 0): a fallback deposit (room base or
              // 2× rent) leaves the column null/0 yet still owes. The owing
              // test is getExpectedDepositEUR(b) > 0, applied below.
              .order("id", { ascending: true })
              .range(from, from + size - 1)
        ),
        // Bookings that already have a live deposit row (open OR paid) get no
        // synthetic entry — the open ones are already in `data`, the paid ones
        // need no action.
        fetchAllRows<{ booking_id: number }>(
          "useOpenInvoices live deposits",
          (from, size) =>
            supabase
              .from("payments")
              .select("booking_id")
              .eq("type", "deposit")
              .not("status", "in", "(canceled,failed)")
              .order("booking_id", { ascending: true })
              .range(from, from + size - 1)
        ),
      ]);
      const rows = (data ?? []) as OpenInvoice[];
      const liveDepositBookingIds = new Set(
        (liveDepositRows ?? [])
          .map((r) => Number(r.booking_id))
          .filter((id) => Number.isFinite(id))
      );
      const pendingDepositBookings = (depositBookings ?? []).filter(
        (b) => !liveDepositBookingIds.has(b.id) && getExpectedDepositEUR(b) > 0
      );

      // Profiles for both real invoice tenants and the synthetic deposit
      // tenants. The invoice pool above is no longer capped, so this id list is
      // not either: sent as one `.in()` it would both build an over-long URL
      // and lose every profile past the same PostgREST row cap, leaving those
      // invoices nameless and unfindable in the picker's tenant search. Ask in
      // batches, each one small enough to come back whole — a uuid costs ~37
      // characters of URL.
      const userIds = [
        ...new Set(
          [
            ...rows.map((r) => r.user_id),
            ...pendingDepositBookings.map((b) => b.user_id),
          ].filter(Boolean)
        ),
      ] as string[];
      const byId = new Map<
        string,
        { name: string | null; email: string | null }
      >();
      if (userIds.length > 0) {
        (await fetchProfiles(userIds)).forEach((p: any) =>
          byId.set(p.id, { name: p.name, email: p.email })
        );
      }
      const enriched = rows.map((r) => ({
        ...r,
        tenantName: r.user_id ? (byId.get(r.user_id)?.name ?? null) : null,
        tenantEmail: r.user_id ? (byId.get(r.user_id)?.email ?? null) : null,
      }));
      return [
        ...enriched,
        ...syntheticPendingDeposits(
          pendingDepositBookings,
          liveDepositBookingIds,
          byId
        ),
      ];
    },
  });

  // COL-593: drop test/CoLiviq candidates on prod, in every caller at once
  // (Brain's assign-payment card and the reconciliation assign dialog both
  // read this pool). Same shape as useReconciliationReviewQueue.
  const hideTest = enabled && BRAIN_HIDES_TEST_DATA;
  const { data: filters } = useTestAccountFilters({ enabled: hideTest });
  const data = useMemo(() => {
    if (!hideTest || !query.data) return query.data;
    const emails = (filters ?? []).map((f) => f.email);
    return query.data.filter((inv) => !isOpenInvoiceTestRow(inv, emails));
  }, [hideTest, query.data, filters]);

  return { ...query, data } as typeof query;
}

/** Count of needs_review matches — feeds the nav pip. */
export function useReconciliationReviewCount(enabled: boolean = true): number {
  const { data } = useQuery({
    queryKey: RECONCILIATION_KEYS.reviewCount(),
    enabled,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from("reconciliation_matches")
        .select("id", { count: "exact", head: true })
        .eq("status", "needs_review");
      if (error) return 0;
      return count ?? 0;
    },
  });
  return data ?? 0;
}

export function useReconciliationMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: RECONCILIATION_KEYS.reviewQueue() });
    qc.invalidateQueries({ queryKey: RECONCILIATION_KEYS.reviewCount() });
    // Confirming applies a payment_proofs row that marks the payment paid —
    // refresh every surface reading tenant payments so it shows up there too.
    qc.invalidateQueries({ queryKey: ["v2", "admin-tenant-payments"] });
    qc.invalidateQueries({ queryKey: ["admin-analytics-payments"] });
    // A rent payment closing here is one of the two events that move COL-461
    // payout readiness (the other is settling the offset invoice, which
    // already invalidates these). Confirming the match that completes an
    // owner's last outstanding rent is exactly when the "[Month] payouts
    // ready" tile should light up, so it must not keep serving the count it
    // computed before the payment landed.
    qc.invalidateQueries({ queryKey: [PROPERTY_RENT_PAID_STATS_KEY] });
    invalidateOwnerCaches(qc);
  };
  // COL-508: a confirm or assign can now pay several months and the tenancy's
  // Mahngebühren, not just the one invoice — so the open-invoice pool the
  // split preview reads, and the reminders page (open months + fees owed),
  // are stale after either.
  const invalidateAfterSplit = () => {
    invalidate();
    qc.invalidateQueries({ queryKey: RECONCILIATION_KEYS.openInvoices() });
    qc.invalidateQueries({ queryKey: ["admin-reminders"] });
  };

  // COL-508: `feeCents` null lets the server apply the split rule (oldest
  // open rent, then its Mahngebühren, then the other months); a number is the
  // admin stating the fee portion. `creditNext` false keeps any leftover in the
  // queue instead of crediting the next rent. Both RPCs return what they
  // actually booked — the toast reports that, never the preview's prediction.
  const toRpcFee = (feeCents: number | null | undefined) =>
    feeCents == null ? null : Math.max(0, Math.round(feeCents));

  const confirm = useMutation({
    mutationFn: async (vars: {
      matchId: string;
      feeCents?: number | null;
      creditNext?: boolean;
    }): Promise<BookedTransferSplit | null> => {
      const { data, error } = await supabase.rpc(
        "confirm_reconciliation_match",
        {
          p_match_id: vars.matchId,
          p_fee_cents: toRpcFee(vars.feeCents),
          p_credit_next: vars.creditNext ?? true,
        }
      );
      if (error) throw error;
      return parseBookedSplit(data);
    },
    onSuccess: invalidateAfterSplit,
  });

  const reject = useMutation({
    mutationFn: async ({
      matchId,
      reason,
    }: {
      matchId: string;
      reason: string;
    }) => {
      const { error } = await supabase.rpc("reject_reconciliation_match", {
        p_match_id: matchId,
        p_reason: reason,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  // COL-398: point the match at an explicitly chosen open invoice — the RPC
  // validates the target is still open and re-attributes with a full audit
  // trail. COL-508: the chosen invoice identifies the tenancy, and the transfer
  // is split across it exactly like confirm.
  const assign = useMutation({
    mutationFn: async ({
      matchId,
      paymentId,
      note,
      feeCents,
      creditNext,
      feeAdjustment,
    }: {
      matchId: string;
      paymentId: string;
      note?: string;
      feeCents?: number | null;
      creditNext?: boolean;
      // COL-637: the Mahngebühren the tenancy owes after this transfer, as the
      // admin set it. Booked in the same transaction as the assignment.
      feeAdjustment?: { feesAfterCents: number; reason: string } | null;
    }): Promise<BookedTransferSplit | null> => {
      // A synthetic pending-deposit entry has no payments row yet — materialise
      // the real pending deposit first, then assign points at it. See
      // syntheticPendingDeposits / materialize_pending_deposit.
      let resolvedPaymentId = paymentId;
      if (paymentId.startsWith(PENDING_DEPOSIT_PREFIX)) {
        const bookingId = Number(
          paymentId.slice(PENDING_DEPOSIT_PREFIX.length)
        );
        // Not in the generated types until this migration is deployed and
        // types are regenerated (npm run supabase:types:*); matches the
        // (supabase as any).rpc pattern used for other fresh RPCs.
        const { data: pid, error: mErr } = await (supabase as any).rpc(
          "materialize_pending_deposit",
          { p_booking_id: bookingId }
        );
        if (mErr) throw mErr;
        if (!pid)
          throw new Error("Could not create the deposit invoice to assign");
        resolvedPaymentId = pid as string;
      }
      const args = {
        p_match_id: matchId,
        p_payment_id: resolvedPaymentId,
        p_note: note ?? null,
        p_fee_cents: toRpcFee(feeCents),
        p_credit_next: creditNext ?? true,
      };
      // COL-637: not in the generated types until the migration is deployed
      // and types are regenerated — same (supabase as any).rpc pattern as above.
      const { data, error } = feeAdjustment
        ? await (supabase as any).rpc(
            "assign_reconciliation_match_with_fee_adjustment",
            {
              ...args,
              p_fees_after_cents: Math.max(
                0,
                Math.round(feeAdjustment.feesAfterCents)
              ),
              p_fee_reason: feeAdjustment.reason,
            }
          )
        : await supabase.rpc("assign_reconciliation_match", args);
      if (error) throw error;
      return parseBookedSplit(data);
    },
    onSuccess: invalidateAfterSplit,
  });

  return { confirm, reject, assign };
}
